import React, { useState } from 'react';
import { Mail, Phone, MapPin, Instagram, Linkedin, Facebook, Send } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { LOGO, SOCIALS } from '../mock';
import useReveal from '../hooks/useReveal';

export default function Contact() {
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [ref, inView] = useReveal(0.05);

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast({ title: 'Missing fields', description: 'Please fill in name, email and message.' });
      return;
    }
    toast({ title: 'Message sent (mock)', description: 'We will be in touch shortly.' });
    setForm({ name: '', email: '', message: '' });
  };

  return (
    <section id="contact" ref={ref} className="section" style={{ position: 'relative', overflow: 'hidden' }}>
      <div className="container-x relative" style={{ zIndex: 2 }}>
        <div className="grid lg:grid-cols-12 gap-8 items-end" style={{ marginBottom: 60 }}>
          <div className={`lg:col-span-7 reveal ${inView ? 'in-view' : ''}`}>
            <div className="eyebrow" style={{ marginBottom: 24 }}>§ 05 · Get In Touch</div>
            <h2 className="section-title">Start your <em>project</em>.</h2>
          </div>
          <div className={`lg:col-span-5 reveal delay-2 ${inView ? 'in-view' : ''}`}>
            <p className="font-serif-i" style={{ color: 'var(--smoke)', fontSize: 17, lineHeight: 1.6 }}>
              Ready to elevate your corporate brand&rsquo;s visual presence? Let&rsquo;s build something remarkable together.
            </p>
          </div>
        </div>

        <div className="grid lg:grid-cols-12 gap-8">
          {/* Left — hanging shop sign */}
          <div className={`lg:col-span-5 reveal from-left ${inView ? 'in-view' : ''}`}>
            <div style={{ position: 'relative', paddingTop: 90, minHeight: 480 }}>
              <div style={{ position: 'absolute', top: 0, left: '15%', right: '15%', height: 2, background: 'linear-gradient(90deg, transparent, var(--brass), transparent)' }} />
              <div className="chain-sign" style={{ width: '100%' }}>
                <div className="chain-bracket left" />
                <div className="chain-bracket right" />
                <div className="chain left" />
                <div className="chain right" />
                <div className="sign-board" style={{ padding: '32px 28px' }}>
                  <div style={{ textAlign: 'center', marginBottom: 20 }}>
                    <img src={LOGO} alt="Vistora" style={{ width: 78, height: 78, borderRadius: 10, background: 'var(--warm-white)', padding: 4, boxShadow: '0 8px 20px -6px rgba(0,0,0,0.6)' }} />
                    <div className="font-mono" style={{ color: 'var(--teal-light)', fontSize: 9, letterSpacing: '0.24em', marginTop: 10 }}>CRAFTING VISUAL IMPACT</div>
                  </div>
                  <div style={{ height: 1, background: 'var(--teal)', opacity: 0.4, marginBottom: 18 }} />
                  {[
                    { Icon: Mail, label: 'Email', value: 'business@vistora.net.in', href: 'mailto:business@vistora.net.in' },
                    { Icon: Phone, label: 'Phone', value: '+91 82176 11049', href: 'tel:+918217611049' },
                    { Icon: MapPin, label: 'Presence', value: 'Pan-India — Bengaluru HQ', href: null },
                  ].map((it) => (
                    <a key={it.label} href={it.href || '#'} style={{ display: 'flex', gap: 14, alignItems: 'center', textDecoration: 'none', padding: '10px 0' }}>
                      <it.Icon size={16} color="var(--teal-light)" strokeWidth={1.8} />
                      <div>
                        <div className="font-mono" style={{ color: 'var(--muted)', fontSize: 9, letterSpacing: '0.22em' }}>{it.label}</div>
                        <div style={{ color: 'var(--paper)', fontSize: 14 }}>{it.value}</div>
                      </div>
                    </a>
                  ))}
                  <div style={{ borderTop: '1px dashed var(--teal-2)', marginTop: 20, paddingTop: 16, display: 'flex', justifyContent: 'center', gap: 12 }}>
                    {[
                      { Icon: Instagram, href: SOCIALS.instagram, label: 'Instagram' },
                      { Icon: Linkedin, href: SOCIALS.linkedin, label: 'LinkedIn' },
                      { Icon: Facebook, href: SOCIALS.facebook, label: 'Facebook' },
                    ].map(({ Icon, href, label }) => (
                      <a key={label} href={href} target="_blank" rel="noopener noreferrer" aria-label={label} style={{
                        width: 36, height: 36, border: '1px solid var(--teal-2)', background: 'transparent',
                        color: 'var(--teal-light)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        transition: 'all 0.3s', borderRadius: '50%', textDecoration: 'none',
                      }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--orange)'; e.currentTarget.style.color = 'var(--warm-white)'; e.currentTarget.style.borderColor = 'var(--orange)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--teal-light)'; e.currentTarget.style.borderColor = 'var(--teal-2)'; }}
                      >
                        <Icon size={14} strokeWidth={1.8} />
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right - Form */}
          <form onSubmit={submit} className={`lg:col-span-7 reveal from-right ${inView ? 'in-view' : ''}`} style={{ background: 'var(--ink-2)', border: '1px solid var(--line)', padding: 44, borderRadius: 6, display: 'flex', flexDirection: 'column', gap: 22 }}>
            <div className="font-mono" style={{ color: 'var(--teal-light)', fontSize: 10, letterSpacing: '0.24em' }}>
              PROJECT BRIEF · FORM.001
            </div>
            <div>
              <label className="field-label">Name</label>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="field-input" placeholder="Your full name" />
            </div>
            <div>
              <label className="field-label">Email</label>
              <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="field-input" placeholder="you@company.com" />
            </div>
            <div style={{ flex: 1 }}>
              <label className="field-label">Tell us about your project</label>
              <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="field-input" style={{ minHeight: 160, resize: 'vertical' }} placeholder="Corporate lobby signage, reception branding, wayfinding, facade rebrand..." />
            </div>
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="font-mono" style={{ color: 'var(--muted)', fontSize: 10, letterSpacing: '0.2em' }}>WE REPLY WITHIN 24 HOURS</div>
              <button type="submit" className="btn-primary">Send Brief <Send size={14} strokeWidth={2.5} /></button>
            </div>
          </form>
        </div>

        <footer style={{ marginTop: 100, paddingTop: 40, borderTop: '1px solid var(--line)' }}>
          <div className="grid md:grid-cols-3 gap-8 items-center">
            <div className="flex items-center gap-3">
              <img src={LOGO} alt="Vistora" style={{ width: 34, height: 34, borderRadius: 6, background: 'var(--warm-white)', padding: 2 }} />
              <div>
                <div className="font-display" style={{ color: 'var(--paper)', fontSize: 20, letterSpacing: '0.14em', lineHeight: 1 }}>VISTORA</div>
                <div className="font-mono" style={{ color: 'var(--teal-light)', fontSize: 8, letterSpacing: '0.24em', marginTop: 3 }}>CRAFTING VISUAL IMPACT</div>
              </div>
            </div>
            <div className="font-serif-i text-center" style={{ color: 'var(--smoke)', fontSize: 14 }}>
              Corporate Signage Studio · est. 2024
            </div>
            <div className="font-mono md:text-right" style={{ color: 'var(--muted)', fontSize: 10, letterSpacing: '0.18em' }}>
              © {new Date().getFullYear()} · VISTORA BRANDING SOLUTIONS PVT. LTD.
            </div>
          </div>
        </footer>
      </div>
    </section>
  );
}
